import { CollapsibleFilters } from '../components/common/CollapsibleFilters';
import { CashDesk, ProfitDeskPortfolio } from '../components/billing/ProfitDesk';
import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Button,
  FormControl,
  FormLabel,
  Input,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/joy'
import { ResponsiveTable } from '../components/common/ResponsiveTable'
import {
  TrendUp as TrendingUp,
  Receipt,
  CreditCard as Payment,
  ChartBar as Assessment,
  DownloadSimple as Download,
  ArrowLeft,
} from '@phosphor-icons/react'
import { analyticsApi } from '../lib/billingApi'
import { useNavigate } from 'react-router-dom'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

export default function BillingReports() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'revenue')
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0],
  })

  // Fetch revenue report
  const { data: revenueData, isLoading: revenueLoading } = useQuery({
    queryKey: ['revenue-report', dateRange],
    queryFn: () => analyticsApi.revenueReport({
      date_from: dateRange.from,
      date_to: dateRange.to,
      grouping: 'month',
    }),
    enabled: activeTab === 'revenue',
  })

  // Fetch aging report
  const { data: agingData, isLoading: agingLoading } = useQuery({
    queryKey: ['aging-report'],
    queryFn: () => analyticsApi.agingReport(),
    enabled: activeTab === 'aging',
  })

  // Fetch profitability report
  const { data: profitData, isLoading: profitLoading, error: profitError } = useQuery({
    queryKey: ['profit-report', dateRange],
    queryFn: () => analyticsApi.profitabilityReport({
      date_from: dateRange.from,
      date_to: dateRange.to,
    }),
    enabled: activeTab === 'profitability',
  })

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num)
  }

  const COLORS = ['#0B6BCB', '#06A77D', '#F9A825', '#E91E63', '#9C27B0']

  // Export functions
  const exportToCSV = () => {
    let csvContent = ''

    switch (activeTab) {
      case 'revenue':
        if (!revenueData) return
        csvContent = 'Revenue Report\n'
        csvContent += `Date Range: ${dateRange.from} to ${dateRange.to}\n\n`
        csvContent += `Total Revenue,${revenueData.total_revenue}\n`
        csvContent += `Invoices Sent,${revenueData.invoices_sent}\n`
        csvContent += `Average Invoice,${revenueData.average_invoice}\n`
        csvContent += `Collection Rate,${revenueData.collection_rate}%\n\n`

        if (revenueData.monthly_data) {
          csvContent += 'Month,Revenue,Collected\n'
          revenueData.monthly_data.forEach((row: any) => {
            csvContent += `${row.month},${row.revenue},${row.collected}\n`
          })
        }
        break

      case 'aging':
        if (!agingData) return
        csvContent = 'Aging Report\n\n'
        csvContent += `Total Outstanding,${agingData.total_outstanding}\n`
        csvContent += `Average Days Outstanding,${agingData.average_days_outstanding}\n`
        csvContent += `Invoices Overdue,${agingData.invoices_overdue}\n\n`
        csvContent += 'Age Range,Amount\n'
        csvContent += `Current,${agingData.summary.current}\n`
        csvContent += `1-30 Days,${agingData.summary.days_1_30}\n`
        csvContent += `31-60 Days,${agingData.summary.days_31_60}\n`
        csvContent += `61-90 Days,${agingData.summary.days_61_90}\n`
        csvContent += `Over 90 Days,${agingData.summary.days_over_90}\n`

        if (agingData.details && agingData.details.length > 0) {
          csvContent += '\nInvoice Details\n'
          csvContent += 'Invoice #,Customer,Amount,Days Overdue\n'
          agingData.details.forEach((inv: any) => {
            csvContent += `${inv.document_number},${inv.contact_name},${inv.balance_due},${inv.days_overdue}\n`
          })
        }
        break

      case 'profitability':
        if (!profitData) return
        csvContent = 'Profitability Report\n'
        csvContent += `Date Range: ${dateRange.from} to ${dateRange.to}\n\n`
        csvContent += `Lifetime contracted value,${profitData.gross_revenue}\n`
        csvContent += `Total Costs,${profitData.total_costs}\n`
        csvContent += `Recorded gross profit,${profitData.net_profit}\n`
        csvContent += `Profit Margin,${profitData.profit_margin}%\n`
        break
    }

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${activeTab}_report_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  const exportToPDF = async () => {
    // For now, we'll just show an alert. In a real implementation,
    // this would call a backend endpoint to generate a PDF
    alert('PDF export will be available in a future update. Please use CSV export for now.')
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Back Button */}
      <Button
        variant="plain"
        color="neutral"
        startDecorator={<ArrowLeft />}
        onClick={() => navigate('/reports')}
        sx={{ mb: 3 }}
      >
        Back to Reports
      </Button>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography level="h1" sx={{ fontSize: { xs: '1.5rem', md: '2.25rem' } }}>
          Financial Reports
        </Typography>
        <Typography level="body-md" sx={{ color: 'text.secondary' }}>
          Analyze your billing data and financial performance
        </Typography>
      </Box>

      {/* Date Range Filter */}
      {['revenue', 'profitability'].includes(activeTab) && <CollapsibleFilters><Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flex: { md: 'none' } }}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>From Date</FormLabel>
                <Input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>To Date</FormLabel>
                <Input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                />
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const now = new Date()
                  setDateRange({
                    from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
                    to: now.toISOString().split('T')[0],
                  })
                }}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                This Month
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const now = new Date()
                  setDateRange({
                    from: new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0],
                    to: now.toISOString().split('T')[0],
                  })
                }}
                sx={{ flex: { xs: 1, md: 'none' } }}
              >
                This Year
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card></CollapsibleFilters>}

      {/* Report Tabs */}
      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as string)}>
        <TabList
          sx={{
            flexWrap: 'wrap',
            '& > *': { flex: { xs: '1 1 45%', sm: '0 0 auto' } },
          }}
        >
          <Tab value="revenue">
            <TrendingUp style={{ marginRight: 8 }} />
            Revenue Report
          </Tab>
          <Tab value="aging">
            <Receipt style={{ marginRight: 8 }} />
            Aging Report
          </Tab>
          <Tab value="profit-desk">Job Profit Desk</Tab>
          <Tab value="cash-forecast">Cash Forecast</Tab>
          <Tab value="profitability">
            <Assessment style={{ marginRight: 8 }} />
            Profitability
          </Tab>
          <Tab value="payments">
            <Payment style={{ marginRight: 8 }} />
            Payment Analysis
          </Tab>
        </TabList>

        {/* Revenue Report Tab */}
        <TabPanel value="revenue">
          <Grid container spacing={3}>
            <Grid xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>Revenue Overview</Typography>
                  {revenueLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : revenueData ? (
                    <>
                      {/* Summary Cards */}
                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid xs={12} sm={6} md={3}>
                          <Card variant="soft" color="primary">
                            <CardContent>
                              <Typography level="body-sm">Total Revenue</Typography>
                              <Typography level="h3">
                                {formatCurrency(revenueData.total_revenue || 0)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                          <Card variant="soft" color="success">
                            <CardContent>
                              <Typography level="body-sm">Invoices Sent</Typography>
                              <Typography level="h3">{revenueData.invoices_sent || 0}</Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                          <Card variant="soft" color="warning">
                            <CardContent>
                              <Typography level="body-sm">Average Invoice</Typography>
                              <Typography level="h3">
                                {formatCurrency(revenueData.average_invoice || 0)}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid xs={12} sm={6} md={3}>
                          <Card variant="soft" color="neutral">
                            <CardContent>
                              <Typography level="body-sm">Collection Rate</Typography>
                              <Typography level="h3">
                                {revenueData.collection_rate || 0}%
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>

                      {/* Revenue Chart */}
                      {revenueData.monthly_data && (
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart data={revenueData.monthly_data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value as string)} />
                            <Legend />
                            <Bar dataKey="revenue" fill="#0B6BCB" name="Revenue" />
                            <Bar dataKey="collected" fill="#06A77D" name="Collected" />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </>
                  ) : (
                    <Alert color="warning">No revenue data available for the selected period</Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Aging Report Tab */}
        <TabPanel value="aging">
          <Grid container spacing={3}>
            <Grid xs={12} lg={8}>
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>Outstanding Invoices by Age</Typography>
                  {agingLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : agingData ? (
                    <>
                      <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Current', value: parseFloat(agingData.summary.current) },
                              { name: '1-30 Days', value: parseFloat(agingData.summary.days_1_30) },
                              { name: '31-60 Days', value: parseFloat(agingData.summary.days_31_60) },
                              { name: '61-90 Days', value: parseFloat(agingData.summary.days_61_90) },
                              { name: 'Over 90 Days', value: parseFloat(agingData.summary.days_over_90) },
                            ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value }) => `${name}: ${formatCurrency(Number(value) || 0)}`}
                            outerRadius={150}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {COLORS.map((color, index) => (
                              <Cell key={`cell-${index}`} fill={color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatCurrency(value as number)} />
                        </PieChart>
                      </ResponsiveContainer>

                      <Divider sx={{ my: 3 }} />

                      {/* Aging Details Table */}
                      {agingData.details && agingData.details.length > 0 && (
                        <>
                          <Typography level="title-md" sx={{ mb: 2 }}>Invoice Details</Typography>
                          <ResponsiveTable<any>
                            rows={agingData.details}
                            getRowKey={(invoice) => invoice.id}
                            columns={[
                              {
                                key: 'document_number',
                                sortValue: (row) => row.document_number,
                                header: 'Invoice #',
                                mobileVariant: 'header',
                                render: (i) => <Typography level="title-sm">{i.document_number}</Typography>,
                              },
                              {
                                key: 'contact_name',
                                sortValue: (row) => row.contact_name,
                                header: 'Customer',
                                render: (i) => <Typography level="body-sm">{i.contact_name}</Typography>,
                              },
                              {
                                key: 'balance_due',
                                sortValue: (row) => Number(row.balance_due),
                                header: 'Amount',
                                render: (i) => <Typography level="body-sm">{formatCurrency(i.balance_due)}</Typography>,
                              },
                              {
                                key: 'days_overdue',
                                sortValue: (row) => row.days_overdue,
                                header: 'Days Overdue',
                                render: (i) => (
                                  <Chip
                                    size="sm"
                                    color={
                                      i.days_overdue <= 0 ? 'success' :
                                      i.days_overdue <= 30 ? 'warning' :
                                      'danger'
                                    }
                                  >
                                    {i.days_overdue} days
                                  </Chip>
                                ),
                              },
                              {
                                key: 'status',
                                header: 'Status',
                                render: () => (
                                  <Chip size="sm" variant="soft" color="danger">
                                    Overdue
                                  </Chip>
                                ),
                              },
                            ]}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <Alert color="warning">No aging data available</Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} lg={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-lg" sx={{ mb: 3 }}>Aging Summary</Typography>
                  {agingData && (
                    <Stack spacing={2}>
                      <Box>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Total Outstanding</Typography>
                        <Typography level="h3" color="danger">
                          {formatCurrency(agingData.total_outstanding || 0)}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Average Days Outstanding</Typography>
                        <Typography level="h4">
                          {agingData.average_days_outstanding || 0} days
                        </Typography>
                      </Box>
                      <Box>
                        <Typography level="body-sm" sx={{ mb: 1 }}>Invoices Overdue</Typography>
                        <Typography level="h4">
                          {agingData.invoices_overdue || 0}
                        </Typography>
                      </Box>
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value="profit-desk"><ProfitDeskPortfolio /></TabPanel>
        <TabPanel value="cash-forecast"><CashDesk /></TabPanel>
        {/* Profitability Tab */}
        <TabPanel value="profitability">
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 3 }}>Profitability Analysis</Typography>
              {profitError ? <Alert color="danger">Could not load profitability. Check the date range and try again.</Alert> : profitLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : profitData ? (
                <>
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid xs={12} sm={4}>
                      <Card variant="soft" color="success">
                        <CardContent>
                          <Typography level="body-sm">Contracted value</Typography>
                          <Typography level="h3">
                            {formatCurrency(profitData.gross_revenue || 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid xs={12} sm={4}>
                      <Card variant="soft" color="danger">
                        <CardContent>
                          <Typography level="body-sm">Total Costs</Typography>
                          <Typography level="h3">
                            {formatCurrency(profitData.total_costs || 0)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid xs={12} sm={4}>
                      <Card variant="soft" color="primary">
                        <CardContent>
                          <Typography level="body-sm">Recorded gross profit</Typography>
                          <Typography level="h3">
                            {formatCurrency(profitData.net_profit || 0)}
                          </Typography>
                          <Typography level="body-sm">
                            {profitData.profit_margin || 0}% margin
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Alert color="neutral">{profitData.note}</Alert>
                  {profitData.warnings?.map((message: string) => <Alert color="warning" key={message}>{message}</Alert>)}
                  {/* Gross profit grouped by actual job board */}
                  {profitData.by_job_type && (
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart data={profitData.by_job_type}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="type" />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value as string)} />
                        <Legend />
                        <Bar dataKey="revenue" fill="#0B6BCB" name="Revenue" />
                        <Bar dataKey="profit" fill="#06A77D" name="Profit" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </>
              ) : (
                <Alert color="neutral">
                  Profitability analysis requires cost data from jobs and materials.
                  This feature will be available once job costing is configured.
                </Alert>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Payment Analysis Tab */}
        <TabPanel value="payments">
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-lg" sx={{ mb: 3 }}>Payment Analysis</Typography>
              <Alert color="neutral">
                Payment analysis showing payment methods, timing, and customer payment behavior coming soon.
              </Alert>
            </CardContent>
          </Card>
        </TabPanel>
      </Tabs>

      {/* Export Actions */}
      {!['profit-desk', 'cash-forecast'].includes(activeTab) && <Box
        sx={{
          mt: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'flex-end',
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          startDecorator={<Download />}
          onClick={exportToCSV}
          disabled={
            (activeTab === 'revenue' && !revenueData) ||
            (activeTab === 'aging' && !agingData) ||
            (activeTab === 'profitability' && !profitData) ||
            ['payments', 'profit-desk', 'cash-forecast'].includes(activeTab)
          }
        >
          Export CSV
        </Button>
        <Button
          variant="solid"
          startDecorator={<Download />}
          onClick={exportToPDF}
          disabled={
            (activeTab === 'revenue' && !revenueData) ||
            (activeTab === 'aging' && !agingData) ||
            (activeTab === 'profitability' && !profitData) ||
            ['payments', 'profit-desk', 'cash-forecast'].includes(activeTab)
          }
        >
          Export PDF
        </Button>
      </Box>}
    </Box>
  )
}

// Grid component
const Grid: React.FC<{
  children: React.ReactNode
  container?: boolean
  spacing?: number
  xs?: number
  sm?: number
  md?: number
  lg?: number
  sx?: any
}> = ({ children, container, spacing = 0, xs, sm, md, lg, sx }) => {
  if (container) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: spacing ? -spacing / 2 : 0,
          ...sx,
        }}
      >
        {children}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        padding: spacing ? spacing / 2 : 0,
        width: {
          xs: xs ? `${(xs / 12) * 100}%` : '100%',
          sm: sm ? `${(sm / 12) * 100}%` : undefined,
          md: md ? `${(md / 12) * 100}%` : undefined,
          lg: lg ? `${(lg / 12) * 100}%` : undefined,
        },
      }}
    >
      {children}
    </Box>
  )
}
